import React, { useState, useEffect } from "react";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
	Button,
	TextField,
	MuiThemeProvider,
	createMuiTheme,
	FormControlLabel,
	Checkbox,
	FormControl,
	InputLabel,
	FormHelperText,
} from "@material-ui/core";
import Select from 'react-select';
import { useHistory, useParams } from "react-router-dom";
import {
	deleteRegatta,
	getRegattaById,
	postRegatta,
	updateRegatta,
} from "../../../../api/regatta";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertInfo, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { getClubs } from '../../../../api/club';
import PreviewDialog from '../../../components/dialogs/PreviewDialog';
import { SERVER_URL } from '../../../../utils';
import Editor from '../../../components/editor/Editor'
import { Col, Row } from "react-bootstrap";
import { FINALIZED_REGATTA_STATE, IN_PROGRESS_REGATTA_STATE, INIT_REGATTA_STATE } from "../../../../constants/utils.constants";

// Create theme for delete button (red)
const theme = createMuiTheme({
	palette: {
		secondary: {
			main: "#F64E60",
		},
	},
});

function getEmptyRegatta() {
	return {
		fullName: "",
		numBoats: "",
		active: true,
		sponsor: {
			name: "",
			logo: "",
			website: "",
		},
		regattaDate: "",
		endDate: "",
		description: "",
		instructions: "",
		announcement: "",
		longitude: null,
		latitude: null,
		state: INIT_REGATTA_STATE,
	};
}

const states = [INIT_REGATTA_STATE, IN_PROGRESS_REGATTA_STATE, FINALIZED_REGATTA_STATE];

export default function EditRegattasPage() {
	const [regatta, setRegatta] = useState(getEmptyRegatta());
	const [clubs, setClubs] = useState(null);
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const [selectedImage, setSelectedImage] = useState(null);
	const [selectedInstructions, setSelectedInstructions] = useState(null);
	const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
	const [previewInstructionsOpen, setPreviewInstructionsOpen] = useState(false);
	const [previewAnnouncementOpen, setPreviewAnnouncementOpen] = useState(false);
	const [openPreviewImage, setOpenPreviewImage] = useState(false);
	const regattaId = useParams().id;
	const history = useHistory();

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		getClubs().then((res) => {
			if (res.status === 200) {
				setClubs(res.data);
			}
		});
		if (!regattaId) {
			disableLoadingData();
			return;
		}
		getRegattaById(regattaId)
			.then((res) => {
				if (res.status === 200) {
					const regattaData = res.data;

					if (regattaData.regattaDate) {
						regattaData.regattaDate = new Date(regattaData.regattaDate)
							.toISOString()
							.slice(0, 10);
					}

					if (regattaData.endDate) {
						regattaData.endDate = new Date(regattaData.endDate)
							.toISOString()
							.slice(0, 10);
					}

					// API devuelve club populate como objeto; react-select necesita el _id
					if (
						regattaData.club != null &&
						typeof regattaData.club === "object" &&
						regattaData.club._id != null
					) {
						regattaData.club = regattaData.club._id;
					}

					setRegatta(regattaData);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "Could not get regatta.",
				});
				history.push("/regattas");
			});
	}, [regattaId, disableLoadingData, history]);

	function saveRegatta() {
		if (!regattaId) {
			postRegatta(regatta, selectedImage, selectedInstructions, selectedAnnouncement)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({
							title: "Saved!",
							customMessage: "Regatta successfully created.",
						});
						history.push("/regattas");
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save regatta.",
					});
				});
		} else {
			updateRegatta(regattaId, regatta, selectedImage, selectedInstructions, selectedAnnouncement)
				.then((res) => {
					if (res.status === 200) {
						alertSuccess({
							title: "Saved!",
							customMessage: "Changes successfully saved.",
						});
						history.push("/regattas");
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save changes.",
					});
				});
		}
	}

	const handleChange = (element) => (event) => {
		setRegatta({ ...regatta, [element]: event.target.value });
	};

	const handleChangeClubs = (new_selected) => {
		setRegatta((prevRegatta) => ({
			...prevRegatta,
			club: new_selected?.value || null,
		}));
	};

	const handleChangeState = (new_selected) => {
		setRegatta((prevRegatta) => ({
			...prevRegatta,
			state: new_selected?.value || null,
		}));
	};
	
	const handleChangeEditor = (element, value) => {
		setRegatta({ ...regatta, [element]: value })
	}

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
					<CardHeader title="Edit regatta"></CardHeader>
					<CardBody>
						<TextField
							id={`fullName`}
							label="Full name"
							value={regatta.fullName}
							onChange={handleChange("fullName")}
							InputLabelProps={{
								shrink: true,
							}}
							margin="normal"
							variant="outlined"
							required
							/>
						<Row>
							<Col>
							<TextField
								id="regattaDate"
								label="Regatta Date"
								type="date"
								value={regatta.regattaDate}
								onChange={handleChange("regattaDate")}
								InputLabelProps={{
								shrink: true,
								}}
								margin="normal"
								variant="outlined"
								required
								fullWidth
							/>
							</Col>
							<Col>
							<TextField
								id="startTime"
								label="Start time"
								type="time"
								value={regatta.startTime}
								onChange={handleChange("startTime")}
								InputLabelProps={{
								shrink: true,
								}}
								margin="normal"
								variant="outlined"
								required
								fullWidth
							/>
							</Col>
						</Row>
						<Row>
							<Col>
							<TextField
								id="endDate"
								label="Regatta End Date"
								type="date"
								value={regatta.endDate}
								onChange={handleChange("endDate")}
								InputLabelProps={{
								shrink: true,
								}}
								margin="normal"
								variant="outlined"
								required
								fullWidth
							/>
							</Col>
							<Col>
							<TextField
								id="endTime"
								label="End time"
								type="time"
								value={regatta.endTime}
								onChange={handleChange("endTime")}
								InputLabelProps={{
								shrink: true,
								}}
								margin="normal"
								variant="outlined"
								required
								fullWidth
							/>
							</Col>
						</Row>
						<TextField
							id={`longitude`}
							label="Longitude"
							type="number"
							value={regatta.longitude}
							onChange={handleChange("longitude")}
							InputLabelProps={{
								shrink: true,
							}}
							margin="normal"
							variant="outlined"
							required
						/>
						<TextField
							id={`latitude`}
							label="Latitude"
							type="number"
							value={regatta.latitude}
							onChange={handleChange("latitude")}
							InputLabelProps={{
								shrink: true,
							}}
							margin="normal"
							variant="outlined"
							required
						/>

						
						<FormControl style={{ width: '100%' }}>
							<InputLabel id='demo-simple-select-standard-label'>
								Content type
							</InputLabel>
							<Select
								options={states?.map((state) => ({
									label: state,
									value: state,
								}))}
								value={states
									?.filter((state) => state === regatta?.state)
									.map((state) => ({
										label: state,
										value: state,
									}))}
								onChange={handleChangeState}
								isClearable
								isSearchable
								placeholder={`Regatta state...`}
								className='mb-3 mt-3'
							/>
						</FormControl>

						<FormControl style={{ width: '100%' }}>
							<InputLabel id='demo-simple-select-standard-label'>
								Content type
							</InputLabel>
							<Select
								options={clubs?.map((club) => ({
									label: club.fullName,
									value: club._id,
								}))}
								value={(() => {
									if (!clubs?.length) return null;
									const clubId = regatta.club && typeof regatta.club === 'object'
										? regatta.club._id
										: regatta.club;
									if (clubId == null || clubId === '') return null;
									const found = clubs.find((c) => String(c._id) === String(clubId));
									return found
										? { label: found.fullName, value: found._id }
										: null;
								})()}
								onChange={handleChangeClubs}
								isClearable
								isSearchable
								placeholder={`Select club...`}
								className='mb-3 mt-3'
							/>
						</FormControl>
						<TextField
							id={`numBoats`}
							label="Number boats"
							type={"number"}
							value={regatta.numBoats}
							onChange={handleChange("numBoats")}
							InputLabelProps={{
								shrink: true,
							}}
							margin="normal"
							variant="outlined"
							required
						/>
						<Editor
							body={(regatta.description) || ''}
							setBody={new_body => handleChangeEditor('description', new_body)}
							className='max-height'
							placeholder={'Enter description here...'}
						/>
						<TextField
							label="Sponsor Name"
							value={regatta?.sponsor?.name || ""}
							onChange={(e) =>
								setRegatta({
									...regatta,
									sponsor: {
										...regatta.sponsor,
										name: e.target.value,
									},
								})
							}
							fullWidth
							margin="normal"
							variant="outlined"
						/>

						<TextField
							label="Sponsor Website"
							value={regatta?.sponsor?.website || ""}
							onChange={(e) =>
								setRegatta({
									...regatta,
									sponsor: {
										...regatta.sponsor,
										website: e.target.value,
									},
								})
							}
							fullWidth
							margin="normal"
							variant="outlined"
						/>

						<label className="mt-4" htmlFor="upload-logo">
							<input
								style={{ display: "none" }}
								id="upload-logo"
								name="upload-logo"
								type="file"
								accept="image/*"
								onChange={(e) => {
									setSelectedImage(e.target.files[0]);
								}}
							/>
							<Button
								style={{ marginRight: "15px" }}
								color="secondary"
								component="span"
								variant="outlined"
							>
								{selectedImage || regatta?.sponsor?.logo
									? "Change sponsor logo"
									: "Upload sponsor logo"}
							</Button>
						</label>

						{(selectedImage || regatta?.sponsor?.logo) && (
							<>
								<Button
									onClick={() => setOpenPreviewImage(true)}
									style={{ marginRight: "15px" }}
									variant="outlined"
								>
									{"Preview sponsor logo"}
								</Button>
								<PreviewDialog
									title="Sponsor logo"
									open={openPreviewImage}
									setOpen={setOpenPreviewImage}
									src={
										selectedImage
											? URL.createObjectURL(selectedImage)
											: `${SERVER_URL}/${regatta.sponsor?.logo || ""}`
									}
								/>
							</>
						)}
						<label className="mt-4" htmlFor="upload-instructions">
							<input
								style={{ display: "none" }}
								id="upload-instructions"
								name="upload-instructions"
								type="file"
								accept=".pdf,.doc,.docx"
								onChange={(e) => {
									setSelectedInstructions(e.target.files[0]);
								}}
							/>
							<Button
								style={{ marginRight: "15px" }}
								color="secondary"
								component="span"
								variant="outlined"
							>
								{selectedInstructions || regatta.instructions
									? "Change instructions file"
									: "Upload instructions file"}
							</Button>
						</label>

						{(selectedInstructions || regatta.instructions) && (
							<>
								<Button
									onClick={() => setPreviewInstructionsOpen(true)}
									style={{ marginRight: "15px" }}
									variant="outlined"
								>
									{"Preview instructions"}
								</Button>
								<PreviewDialog
									title="Instructions"
									open={previewInstructionsOpen}
									setOpen={setPreviewInstructionsOpen}
									src={
										selectedInstructions
											? URL.createObjectURL(selectedInstructions)
											: `${SERVER_URL}/${regatta.instructions}`
									}
								/>
							</>
						)}

						<label className="mt-4" htmlFor="upload-announcement">
							<input
								style={{ display: "none" }}
								id="upload-announcement"
								name="upload-announcement"
								type="file"
								accept=".pdf,.doc,.docx"
								onChange={(e) => {
									setSelectedAnnouncement(e.target.files[0]);
								}}
							/>
							<Button
								style={{ marginRight: "15px" }}
								color="secondary"
								component="span"
								variant="outlined"
							>
								{selectedAnnouncement || regatta.announcement
									? "Change announcement file"
									: "Upload announcement file"}
							</Button>
						</label>

						{(selectedAnnouncement || regatta.announcement) && (
							<>
								<Button
									onClick={() => setPreviewAnnouncementOpen(true)}
									style={{ marginRight: "15px" }}
									variant="outlined"
								>
									{"Preview announcement"}
								</Button>
								<PreviewDialog
									title="Announcement"
									open={previewAnnouncementOpen}
									setOpen={setPreviewAnnouncementOpen}
									src={
										selectedAnnouncement
											? URL.createObjectURL(selectedAnnouncement)
											: `${SERVER_URL}/${regatta.announcement}`
									}
								/>
							</>
						)}

						<br />
						<br />
						<FormControlLabel
							control={
								<Checkbox
									checked={regatta.active}
									onChange={() =>
										setRegatta({
											...regatta,
											active: !regatta.active,
										})
									}
									name="checkActive"
								/>
							}
							label="Active"
						/>

					</CardBody>
				</Card>
				<Button
					onClick={() => history.push("/regattas")}
					variant="outlined"
					style={{ marginRight: "20px" }}
				>
					Back
				</Button>
				<Button
					onClick={() => saveRegatta()}
					variant="outlined"
					color="primary"
					style={{ marginRight: "20px" }}
				>
					Save regatta
				</Button>
				{regattaId && (
					<>
						<MuiThemeProvider theme={theme}>
							<Button
								onClick={() => setOpenConfirmDialog(true)}
								variant="outlined"
								color="secondary"
							>
								Delete regatta
							</Button>
						</MuiThemeProvider>

						<ConfirmDialog
							title={"Are you sure you want to delete this regatta?"}
							open={openConfirmDialog}
							setOpen={setOpenConfirmDialog}
							onConfirm={() => {
								deleteRegatta(regattaId)
									.then((res) => {
										if (res.status === 204) {
											alertSuccess({
												title: "Deleted!",
												customMessage:
													"Regatta deleted successfully",
											});
											history.push("/regattas");
										}
									})
									.catch((error) => {
										alertError({
											error: error,
											customMessage:
												"Could not delete economic sector.",
										});
									});
							}}
						/>
					</>
				)}
			</>
		);
}
